import { type TFunction } from 'i18next';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { AppContext } from '@/shared/context/context';
import signalRMessages from '@/redux/actions/signalRMessages';
import useNotification from '@/shared/hooks/UseNotification';
import useSocketEvent from '@/shared/hooks/useSocketEvent';

const parseSocketBoolean = (value: string): boolean => value.toLowerCase() === 'true';

type SendNotification = ReturnType<typeof useNotification>['sendNotification'];

const tryEnqueueImportFinishedMessage = (
  args: string[],
  {
    currentTeamUserId,
    sendNotification,
    t,
    type,
  }: {
    currentTeamUserId?: string | null;
    sendNotification: SendNotification;
    t: TFunction;
    type: string;
  },
) => {
  if (args.length < 5) return;

  const [, , isReadyRaw, createdByUserId, description] = args;

  if (!parseSocketBoolean(isReadyRaw)) return;
  if (!currentTeamUserId || createdByUserId !== currentTeamUserId) return;

  sendNotification({
    header: t('success'),
    message: t('_globalMessages.importFinished', { description, type }),
    options: {
      autoHideDuration: 10000,
    },
    variant: 'success',
  });
};

const useGlobalImportFinishedMessages = () => {
  const { currentTeamUser } = useContext(AppContext);
  const { sendNotification } = useNotification();
  const { t } = useTranslation();

  const deps = {
    currentTeamUserId: currentTeamUser?.userId,
    sendNotification,
    t,
  };

  useSocketEvent(signalRMessages.AccessKeyImportUpdated, (...args: string[]) => {
    tryEnqueueImportFinishedMessage(args, {
      ...deps,
      type: t('_globalMessages.importTypes.accessKey'),
    });
  });

  useSocketEvent(signalRMessages.BuildingImportUpdated, (...args: string[]) => {
    tryEnqueueImportFinishedMessage(args, {
      ...deps,
      type: t('_globalMessages.importTypes.building'),
    });
  });

  useSocketEvent(signalRMessages.BuildingRelationImportUpdated, (...args: string[]) => {
    tryEnqueueImportFinishedMessage(args, {
      ...deps,
      type: t('_globalMessages.importTypes.buildingRelation'),
    });
  });

  useSocketEvent(signalRMessages.CompanyImportUpdated, (...args: string[]) => {
    tryEnqueueImportFinishedMessage(args, {
      ...deps,
      type: t('_globalMessages.importTypes.company'),
    });
  });

  useSocketEvent(signalRMessages.DivisionKeyImportUpdated, (...args: string[]) => {
    tryEnqueueImportFinishedMessage(args, {
      ...deps,
      type: t('_globalMessages.importTypes.divisionKey'),
    });
  });

  useSocketEvent(signalRMessages.GeneralLedgerAccountImportUpdated, (...args: string[]) => {
    tryEnqueueImportFinishedMessage(args, {
      ...deps,
      type: t('_globalMessages.importTypes.generalLedgerAccount'),
    });
  });
};

export default useGlobalImportFinishedMessages;
